import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'
import { Spinner } from '@/components/ui/spinner'
import { AuthProvider } from '@/contexts/AuthContext'
import { GuildRealtimeProvider } from '@/contexts/GuildRealtimeContext'
import ProtectedRoute from '@/components/ProtectedRoute'
import Layout from '@/components/Layout'

const Auth = lazy(() => import('@/pages/Auth'))
const Landing = lazy(() => import('@/pages/Landing'))
const Onboarding = lazy(() => import('@/pages/Onboarding'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Directory = lazy(() => import('@/pages/Directory'))
const Member = lazy(() => import('@/pages/Member'))
const MyProfile = lazy(() => import('@/pages/MyProfile'))
const Projects = lazy(() => import('@/pages/Projects'))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'))
const MyProductions = lazy(() => import('@/pages/MyProductions'))
const ProductionWorkspace = lazy(() => import('@/pages/ProductionWorkspace'))
const OpenRoles = lazy(() => import('@/pages/OpenRoles'))
const Applications = lazy(() => import('@/pages/Applications'))
const Messages = lazy(() => import('@/pages/Messages'))
const Notifications = lazy(() => import('@/pages/Notifications'))
const Invites = lazy(() => import('@/pages/Invites'))

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="size-8 text-muted-foreground" />
    </div>
  )
}

function GuildPage({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <GuildRealtimeProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireProfile={false}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Landing />} />
            <Route path="/dashboard" element={<GuildPage><Dashboard /></GuildPage>} />
            <Route path="/directory" element={<GuildPage><Directory /></GuildPage>} />
            <Route path="/members/:id" element={<GuildPage><Member /></GuildPage>} />
            <Route path="/profile" element={<GuildPage><MyProfile /></GuildPage>} />
            <Route path="/projects" element={<GuildPage><Projects /></GuildPage>} />
            <Route path="/projects/:id" element={<GuildPage><ProjectDetail /></GuildPage>} />
            <Route path="/productions" element={<GuildPage><MyProductions /></GuildPage>} />
            <Route path="/productions/:id" element={<GuildPage><ProductionWorkspace /></GuildPage>} />
            <Route path="/roles" element={<GuildPage><OpenRoles /></GuildPage>} />
            <Route path="/applications" element={<GuildPage><Applications /></GuildPage>} />
            <Route path="/messages" element={<GuildPage><Messages /></GuildPage>} />
            <Route path="/messages/:id" element={<GuildPage><Messages /></GuildPage>} />
            <Route path="/notifications" element={<GuildPage><Notifications /></GuildPage>} />
            <Route path="/invites" element={<GuildPage><Invites /></GuildPage>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
      <Toaster position="bottom-right" />
      </GuildRealtimeProvider>
    </AuthProvider>
  )
}
